import type { ElementType, FC, ReactNode } from 'react'

import { useRef } from 'react'
import {
    FULL_MOTION,
    gsap,
    SplitText,
    useGSAP
} from '@/lib/gsap'

interface SplitRevealProps {
    as?: 'h1' | 'h2' | 'h3' | 'p'
    id?: string
    className?: string
    children: ReactNode
    delay?: number
    start?: string
}

/** Headline that rises into view line by line, each line behind its own mask. */
export const SplitReveal: FC<SplitRevealProps> = ({
    as = 'h2',
    id,
    className,
    children,
    delay = 0,
    start = 'top 86%'
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const Component: ElementType = as

    useGSAP(() => {
        const element = ref.current
        if (!element) return

        const mm = gsap.matchMedia()

        mm.add(FULL_MOTION, () => {
            const split = SplitText.create(element, {
                type: 'lines',
                mask: 'lines',
                linesClass: 'split-line',
                autoSplit: true,
                onSplit: (self) => {
                    return gsap.from(self.lines, {
                        yPercent: 115,
                        duration: 1.3,
                        ease: 'expo.out',
                        stagger: 0.09,
                        delay,
                        scrollTrigger: {
                            trigger: element,
                            start,
                            once: true
                        }
                    })
                }
            })

            return () => split.revert()
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <Component ref={ref} id={id} className={className}>
            {children}
        </Component>
    )
}
